import AuthContext from "../../../../auth/AuthContext";
import AssessmentMasterService from "../../../../services/AssessmentMasterService";
import AlertService from "../../../../services/AlertService";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "primereact/skeleton";
import MasterAssessmentCard from './MasterAssessmentCard';
import SharedService from '../../../../services/SharedService';
import { FaPlus } from "react-icons/fa";

const MasterAssessments = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [permissions, setPermissions] = useState({});
  const authContext = useContext(AuthContext);

  useEffect(() => {
    const assessmentAccess = authContext.getPermissions("assessmentsmaster");
    if (assessmentAccess.filter((f) => f.access.includes(1)).length === 0) {
      navigate("/");
    }
    setPermissions({
      showAdd: assessmentAccess.some(f => f.access.includes(2)),
      showEdit: assessmentAccess.some(f => f.access.includes(3)),
      showDelete: assessmentAccess.some(f => f.access.includes(4)),
    });
  }, [authContext, navigate]);

const loadAssessments = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await AssessmentMasterService.GetAll(
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setAssessments(data.data);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController(); 
    loadAssessments(controller.signal); 
    return () => controller.abort(); 
  }, [loadAssessments, authContext]);

  const handleAddClick = () => {
    sessionStorage.removeItem("assessmentId");
    navigate("/masters-assessments/add");
  };

  const onEditHandler = async (data) => {
    sessionStorage.setItem("assessmentId", data.id);
    navigate(`/masters-assessments/${SharedService.GenerateSlug(data.assessmentName)}/edit`, { state: { id: data.id } });
  };

  return (
    <div className="container-fluid custom-container mt-3">
      {permissions.showAdd && (
        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-primary py-2" onClick={handleAddClick}>
            <FaPlus className="pb-1 me-1" />Create Assessment
          </button>
        </div>
      )}
      <div className="row g-4 mb-4">
        {loading ? (
          Array.from({ length: 8 }).map((_, idx) => (
            <div key={idx} className="col-12 col-sm-6 col-md-4 col-lg-4 col-xl-3">

              <div key={idx} className="card border-0 shadow-sm p-3 p-lg-4 h-100"
                style={{ minHeight: "100px" }}
              >
                <center className="d-flex justify-content-center text-center fw-bold w-100 mb-3">
                  <Skeleton width="50%" height="1.5rem" />
                </center>
                <center className="d-flex justify-content-center text-center fw-bold w-100 mb-2">
                  <Skeleton width="90%" height="1.2rem" />
                </center>
                <center className="d-flex justify-content-center text-center fw-bold w-100 mb-3">
                  <Skeleton width="80%" height="1.2rem" />
                </center>
                <div className="progress mb-3" style={{ "height": "5px" }}>
                  <Skeleton width="100%" height="1.2rem" />
                </div>
                <div className="d-flex justify-content-between mb-4">
                  <Skeleton width="20%" height="1.2rem" />
                  <Skeleton width="30%" height="1.2rem" />
                </div>
                <div className="d-flex justify-content-between mb-4">
                  <Skeleton width="20%" height="1.2rem" />
                  <Skeleton width="20%" height="1.2rem" />
                </div>
                <div className="d-flex justify-content-center">
                  <Skeleton width='80%' height="2rem" borderRadius="20px" />
                </div>
              </div >
            </div>
          ))
        ) :
          assessments.length > 0 ? (
            assessments.map((assessment, i) => (
              <div
                key={assessment.id}
                className="col-12 col-sm-6 col-md-4 col-lg-4 col-xl-3"
              >
                <MasterAssessmentCard
                  data={assessment}
                  no={i}
                  onEdit={permissions.showEdit ? onEditHandler : null}
                  onDelete={permissions.showEdit ? loadAssessments : null}
                />
              </div>
            ))
          ) :
            <div className="col-12 text-center">No assessments available...</div>
        }
      </div>
    </div>
  );
};

export default MasterAssessments;
